// src/pages/SessionsPage.tsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Monitor, Smartphone, Tablet, MapPin, Clock, LogOut, AlertCircle } from 'lucide-react';
import './SessionsPage.css';
import { useNavigate } from 'react-router-dom';

interface Session {
    sessionId: string;
    deviceInfo: {
        browser: string;
        os: string;
        platform: string;
        vendor: string | null;
        model: string | null;
        deviceName: string | null;
    };
    ipAddress: string;
    lastActivityAt: string;
    createdAt: string;
    isCurrent: boolean;
    isRevoked: boolean;        // adicionar
    revokedAt: string | null;  // adicionar
    revokeReason: string | null; // adicionar
}

const SessionsPage: React.FC = () => {
    useAuth();
    const [sessions, setSessions] = useState<Session[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        loadSessions();
    }, []);

    const loadSessions = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch('/api/auth/sessions', {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                setSessions(data.sessions);
            } else {
                setError('Erro ao carregar sessões');
            }
        } catch (err) {
            setError('Erro ao conectar ao servidor');
        } finally {
            setLoading(false);
        }
    };

    const handleRevokeSession = async (sessionId: string) => {
        if (!window.confirm('Deseja encerrar esta sessão?')) return;

        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`/api/auth/sessions/${sessionId}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.ok) {
                loadSessions();
            } else {
                alert('Erro ao revogar sessão');
            }
        } catch (err) {
            alert('Erro ao conectar ao servidor');
        }
    };

    const getDeviceIcon = (platform: string, vendor?: string | null) => {
        if (platform === 'mobile') return <Smartphone size={24} />;
        if (platform === 'tablet') return <Tablet size={24} />;
        return <Monitor size={24} />;
    };

    const getDeviceLabel = (deviceInfo: Session['deviceInfo']) => {
        if (deviceInfo.deviceName && deviceInfo.deviceName !== 'Computador') {
            return deviceInfo.deviceName;
        }
        // Fallback para sessões antigas sem deviceName
        if (deviceInfo.vendor && deviceInfo.model) {
            return `${deviceInfo.vendor} ${deviceInfo.model}`;
        }
        return deviceInfo.browser;
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleString('pt-BR');
    };

    if (loading) {
        return <div className="sp-loading">Carregando sessões...</div>;
    }

    return (
        <div className="sp-page">
            <div className="sp-header">
                <button className="sp-btn-back" onClick={() => navigate('/student-area')}>
                    ← Voltar
                </button>
                <h1>Dispositivos Conectados</h1>
                <p>Gerencie onde sua conta está ativa</p>
            </div>

            {error && (
                <div className="sp-error">
                    <AlertCircle size={20} />
                    <span>{error}</span>
                </div>
            )}

            <div className="sp-info">
                <AlertCircle size={18} />
                <p>
                    <strong>Política de Sessão Única:</strong> Apenas um dispositivo pode estar conectado por vez.
                    Se você fizer login em outro dispositivo, este será desconectado automaticamente.
                </p>
            </div>

            <div className="sp-list">
                {sessions.length === 0 ? (
                    <div className="sp-empty">
                        <Monitor size={48} />
                        <p>Nenhuma sessão ativa encontrada</p>
                    </div>
                ) : (
                    sessions.map(session => (
                        <div key={session.sessionId} className={`sp-card ${session.isCurrent ? 'sp-current' : ''}`}>
                            <div className="sp-icon">
                                {getDeviceIcon(session.deviceInfo.platform, session.deviceInfo.vendor)}
                            </div>

                            <div className="sp-content">
                                <div className="sp-main">
                                    <h3>{getDeviceLabel(session.deviceInfo)}</h3>
                                    {session.isCurrent && <span className="sp-badge">Dispositivo Atual</span>}
                                    {session.isRevoked && <span className="sp-badge sp-badge-revoked">Encerrado</span>}
                                </div>

                                <div className="sp-details">
                                    <div className="sp-detail">
                                        <Monitor size={14} />
                                        <span>{session.deviceInfo.browser} · {session.deviceInfo.os}</span>
                                    </div>

                                    <div className="sp-detail">
                                        <MapPin size={14} />
                                        <span>{session.ipAddress}</span>
                                    </div>

                                    {session.isRevoked && session.revokedAt && (
                                        <div className="sp-detail">
                                            <LogOut size={14} />
                                            <span>Encerrado em: {formatDate(session.revokedAt)}</span>
                                        </div>
                                    )}

                                    <div className="sp-detail">
                                        <Clock size={14} />
                                        <span>Última atividade: {formatDate(session.lastActivityAt)}</span>
                                    </div>
                                </div>

                                <div className="sp-meta">
                                    <span>Conectado desde: {formatDate(session.createdAt)}</span>
                                </div>
                            </div>

                            {!session.isCurrent && !session.isRevoked && (
                                <button className="sp-btn-revoke" onClick={() => handleRevokeSession(session.sessionId)}>
                                    <LogOut size={18} />
                                    <span>Desconectar</span>
                                </button>
                            )}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default SessionsPage;